import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  FiatCurrency,
  HighScarcityStatus as HighScarcityStatusEnum,
} from '@sorare/core/src/__generated__/globalTypes';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';

import type { HighScarcityStatus_highScarcityStatus } from './__generated__/index.graphql.ts';

const Table = styled.table`
  border-spacing: var(--unit);
`;

const StatusSpan = styled.div`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  color: var(--c-black);
  &.${HighScarcityStatusEnum.SILVER} {
    background-color: silver;
  }
  &.${HighScarcityStatusEnum.GOLD} {
    background-color: gold;
  }
  &.${HighScarcityStatusEnum.PLATINUM} {
    background: var(--c-gradient-score-max);
    filter: drop-shadow(0 1px 16px var(--c-score-max-glow));
  }
`;

const Status = ({ status }: { status: HighScarcityStatusEnum | null }) => {
  return (
    <td className="text-center">
      {status ? (
        <StatusSpan className={status}>{status.toLocaleLowerCase()}</StatusSpan>
      ) : (
        '-'
      )}
    </td>
  );
};

type Props = {
  currentUser: HighScarcityStatus_highScarcityStatus;
};

export const HighScarcityStatus = ({ currentUser }: Props) => {
  const { highScarcityStatus } = currentUser;

  if (!highScarcityStatus) return null;

  const {
    current,
    previous,
    spendLeftToNextStatus,
    spendLeftToPreviousPeriodStatus,
  } = highScarcityStatus;

  return (
    <Table>
      <thead>
        <tr>
          <th aria-label="empty" />
          <th>
            <FormattedMessage defaultMessage="Current Season" />
          </th>
          <th>
            <FormattedMessage defaultMessage="Previous Season" />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th className="text-left">
            <FormattedMessage defaultMessage="Status" />
          </th>
          <Status status={current.status} />
          <Status status={previous.status} />
        </tr>
        <tr>
          <th className="text-left">
            <FormattedMessage defaultMessage="Spend" />
          </th>
          <td className="text-center">
            <AmountWithConversion
              monetaryAmount={current.spend}
              forceFiatCurrency={FiatCurrency.EUR}
            />
          </td>
          <td className="text-center">
            <AmountWithConversion
              monetaryAmount={previous.spend}
              forceFiatCurrency={FiatCurrency.EUR}
            />
          </td>
        </tr>
        {current.status !== HighScarcityStatusEnum.PLATINUM && (
          <>
            <tr>
              <th className="text-left">
                <FormattedMessage defaultMessage="Spend Left to Next Status" />
              </th>
              <td className="text-center">
                {spendLeftToNextStatus ? (
                  <AmountWithConversion
                    monetaryAmount={spendLeftToNextStatus}
                    forceFiatCurrency={FiatCurrency.EUR}
                  />
                ) : (
                  '-'
                )}
              </td>
              <td className="text-center">-</td>
            </tr>
            {!!previous.status && (
              <tr>
                <th className="text-left">
                  <FormattedMessage defaultMessage="Spend Left to Previous Period Status" />
                </th>
                <td className="text-center">
                  {spendLeftToPreviousPeriodStatus ? (
                    <AmountWithConversion
                      monetaryAmount={spendLeftToPreviousPeriodStatus}
                      forceFiatCurrency={FiatCurrency.EUR}
                    />
                  ) : (
                    '-'
                  )}
                </td>
                <td className="text-center">-</td>
              </tr>
            )}
          </>
        )}
      </tbody>
    </Table>
  );
};

HighScarcityStatus.fragments = {
  highScarcityStatus: gql`
    fragment HighScarcityStatus_highScarcityStatus on CurrentUser {
      slug
      highScarcityStatus {
        current {
          status
          spend {
            ...useMonetaryAmount_monetaryAmount
          }
        }
        previous {
          status
          spend {
            ...useMonetaryAmount_monetaryAmount
          }
        }
        spendLeftToNextStatus {
          ...useMonetaryAmount_monetaryAmount
        }
        spendLeftToPreviousPeriodStatus {
          ...useMonetaryAmount_monetaryAmount
        }
      }
    }
    ${AmountWithConversion.fragments.monetaryAmount}
  ` as TypedDocumentNode<HighScarcityStatus_highScarcityStatus>,
};
